import { Pencil } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from '../atoms/Icon'
import { Input } from '../atoms/Input'

/**
 * StageRenameInput — inline-editable stage label for the Custom Flows builder.
 *
 * Behaviour:
 *   - Renders a button-styled label by default. Click (or focus + Enter) flips
 *     into edit mode and focuses the underlying Input.
 *   - Enter commits the trimmed value via `onCommit` (when non-empty + changed).
 *   - Escape reverts the draft back to the original value + fires `onCancel`.
 *   - Blur is treated as "commit" (mirrors Notion / Linear inline-rename).
 *   - Empty / whitespace-only commits are rejected — value reverts silently.
 *   - `maxLength` is enforced via the native attribute (default 64).
 *
 * Visuals match the DS `Input` atom in editing state and use the DS focus ring
 * tokens. No raw colours.
 *
 * Composition: Input (atom) + Icon (atom).
 */

export interface StageRenameInputProps {
  /** Current committed stage name. */
  value: string
  /** Called on Enter / blur with a non-empty trimmed value. */
  onCommit: (next: string) => void
  /** Called on Escape (after the draft is reverted). */
  onCancel?: () => void
  /** Placeholder shown when the input is empty. */
  placeholder?: string
  /** Hard cap on the rename length. Default 64. */
  maxLength?: number
  /** When true the label is read-only; editing cannot be entered. */
  disabled?: boolean
  /** A11Y label for screen readers. Defaults to "Rename stage". */
  'aria-label'?: string
  /** Extra classes for the outer wrapper. */
  className?: string
}

export const StageRenameInput = React.forwardRef<HTMLDivElement, StageRenameInputProps>(
  (
    {
      value,
      onCommit,
      onCancel,
      placeholder = 'Stage name',
      maxLength = 64,
      disabled = false,
      'aria-label': ariaLabel = 'Rename stage',
      className,
    },
    ref,
  ) => {
    const [editing, setEditing] = React.useState(false)
    const [draft, setDraft] = React.useState(value)
    const inputRef = React.useRef<HTMLInputElement | null>(null)

    // Keep the draft in sync if the upstream value changes while idle.
    React.useEffect(() => {
      if (!editing) setDraft(value)
    }, [value, editing])

    // Focus + select all when entering edit mode so a single keystroke replaces.
    React.useEffect(() => {
      if (editing && inputRef.current) {
        inputRef.current.focus()
        inputRef.current.select()
      }
    }, [editing])

    const enterEdit = React.useCallback(() => {
      if (disabled) return
      setDraft(value)
      setEditing(true)
    }, [disabled, value])

    const commit = React.useCallback(() => {
      const next = draft.trim()
      // Reject empty + unchanged commits silently.
      if (!next || next === value) {
        setDraft(value)
        setEditing(false)
        return
      }
      onCommit(next)
      setEditing(false)
    }, [draft, onCommit, value])

    const cancel = React.useCallback(() => {
      setDraft(value)
      setEditing(false)
      onCancel?.()
    }, [onCancel, value])

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          cancel()
        }
      },
      [cancel, commit],
    )

    const handleLabelKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'F2') {
          e.preventDefault()
          enterEdit()
        }
      },
      [enterEdit],
    )

    return (
      <div ref={ref} className={cn('flex max-w-full min-w-0', className)}>
        {editing ? (
          <Input
            ref={inputRef}
            size="sm"
            value={draft}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
            aria-label={ariaLabel}
            // Stop the wrapping <button> from re-capturing pointer events.
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={commit}
            className={cn(
              // Tighter inline footprint than the default 100% width.
              'w-[min(20rem,100%)]',
            )}
          />
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={enterEdit}
            onDoubleClick={enterEdit}
            onKeyDown={handleLabelKeyDown}
            aria-label={ariaLabel}
            // Group lets the pencil icon fade in on hover/focus of the label.
            className={cn(
              'group inline-flex items-center gap-2 max-w-full',
              'rounded-md px-2 py-1 -mx-2 -my-1',
              'text-body-md font-medium text-ia-text',
              'transition-[background-color,color] duration-150',
              'hover:bg-ia-elevated focus-visible:bg-ia-elevated',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <span className={cn('truncate', !value && 'text-ia-text-secondary italic')}>
              {value || placeholder}
            </span>
            <Icon
              icon={Pencil}
              size="xs"
              className={cn(
                'shrink-0 text-ia-text-tertiary',
                'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
                'transition-opacity duration-150',
              )}
            />
          </button>
        )}
      </div>
    )
  },
)
StageRenameInput.displayName = 'StageRenameInput'
